/**
 * @file 微信小程序 WebView 平台策略
 * @description 微信小程序环境的平台能力实现
 */

import { removePubParamsOfUrl, appendSourceFlagToUrl } from '@h5-journey/utils'
import wx from 'weixin-js-sdk'
import qs from 'qs'
import { useAppStore } from '@h5-journey/stores'
import {
  PlatformType,
  type IPlatformStrategy,
  type NavigateToOptions,
  type LoginOptions,
  type ShareData,
  type LocationResult,
  type LocationNavigateOptions
} from '../types'
import { getGeoInfo } from '@h5-journey/service'
import { setRequestFromHeader } from '../request-from'

export function createMiniProgramStrategy(): IPlatformStrategy {
  return {
    type: PlatformType.MINIPROGRAM,

    async onInit({ userStore, options }) {
      // 微信小程序环境：解析 URL 参数并同步到 storage
      const search = window.location.search.replace('?', '')
      const urlParams = (qs.parse(search) || {}) as Record<string, string | undefined>

      const {
        mobile = '',
        memberId = '',
        loginToken = '',
        loginId = '',
        sourceFlag = '',
        showLoginSuccessModal = '',
        client: miniClient = 'wxapp',
        version: miniVersion = 'v6.0.13',
        cuid: miniCuid = '39ca90fa-eed9-4505-a877-65ed397d8199'
      } = urlParams

      // 设置终端编码为微信小程序
      sessionStorage.setItem('terminalCode', '04')

      const appStore = useAppStore()
      appStore.terminalCode = '04'
      if (sourceFlag) {
        // 转为竖线格式存储（query 中为下划线格式）
        const normalized = sourceFlag.replace(/_/g, '|')
        sessionStorage.setItem('sourceFlag', normalized)
        appStore.sourceFlag = normalized
      }

      // 存储 URL 参数供埋点使用
      sessionStorage.setItem('wxappBaseParams', JSON.stringify(urlParams))

      // 设置 Request-From
      setRequestFromHeader({ client: miniClient, version: miniVersion, cuid: miniCuid })

      // 设置用户信息
      userStore.setUserInfo({})
      userStore.setUserInfo({ mobile, memberId, loginId, loginToken })

      // 处理登录成功弹窗
      if (showLoginSuccessModal === '1' && options.onLoginSuccessModal) {
        sessionStorage.setItem('showLoginSuccessModal', '1')
      }

      // 同步用户数据到 sessionStorage
      const syncKeys = { loginToken, memberId, mobile, loginId } as const
      Object.entries(syncKeys).forEach(([key, value]) => {
        if (value) {
          sessionStorage.removeItem(key)
          sessionStorage.setItem(key, value)
          localStorage.removeItem(key)
        }
      })
    },

    navigation: {
      navigateTo(url: string, options: NavigateToOptions = {}) {
        const { hasHistory = true, cb } = options
        // 自动携带 sourceFlag
        const targetUrl = appendSourceFlagToUrl(url)
        const miniProgramUrl = `/pages/webview/index?url=${encodeURIComponent(targetUrl)}`
        try {
          const action = hasHistory ? 'navigateTo' : 'redirectTo'
          wx?.miniProgram?.[action]?.({
            url: miniProgramUrl,
            success: () => {
              cb?.()
            },
            fail: (error: any) => {
              console.error('[Platform:MiniProgram] 跳转失败:', error)
            }
          })
        } catch (error) {
          console.error('[Platform:MiniProgram] 跳转失败:', error)
        }
      },

      goBack() {
        ;(wx?.miniProgram as any)?.navigateBack?.()
      },

      goHome() {
        wx?.miniProgram?.switchTab?.({ url: '/pages/index/index' })
      },

      closePage() {
        ;(wx?.miniProgram as any)?.navigateBack?.()
      },

      async toLogin(options: LoginOptions = {}) {
        const returnUrl = options.returnUrl || removePubParamsOfUrl(location.href)
        const hasHistory = options.hasHistory !== false
        const params = new URLSearchParams({ url: returnUrl })
        if (options.extraParams) {
          Object.entries(options.extraParams).forEach(([key, value]) => {
            params.set(key, value)
          })
        }
        wx?.miniProgram?.[hasHistory ? 'navigateTo' : 'redirectTo']?.({
          url: `/pages/login/index?${params.toString()}`
        })
      }
    },

    communication: {
      makePhoneCall(phoneNumber: string) {
        // 微信小程序 WebView 中使用 window.open 拨号
        window.open(`tel:${phoneNumber}`)
      },

      async share(_data: ShareData) {
        // 小程序 WebView 内分享由小程序原生层控制
        console.warn('[Platform:MiniProgram] WebView 内分享由小程序原生层控制')
      }
    },

    location: {
      /**
       * 微信小程序 WebView 定位实现
       * 方案：wx.getLocation → 经纬度 → 逆地理；失败时抛出错误，由调用方自行处理 fallback
       */
      async getLocation(): Promise<LocationResult> {
        // 1. 小程序获取经纬度
        const { latitude, longitude } = await new Promise<any>((resolve, reject) => {
          wx.getLocation({
            type: 'gcj02',
            success: resolve,
            fail: reject
          })
        })

        // 2. 调用同一个 service 逆地理
        const geoData = await getGeoInfo(latitude, longitude)
        const city = geoData?.regeocode?.addressComponent?.city

        if (!city) {
          throw new Error('[Miniprogram] 逆地理编码未返回城市信息')
        }

        return {
          city,
          latitude,
          longitude,
          message: 'miniprogram success'
        }
      },
      locationNavigate(options: LocationNavigateOptions): void {
        // 产品口径（2026-10-23）：小程序端地图导航不支持，入口已在酒店详情等调用方屏蔽；
        // 本策略保持空实现，勿再接入（曾试过 wx.miniProgram.navigateTo 跳原生查看位置页，未达预期）
        void options
        console.warn('[Platform:Miniprogram] 地图导航不支持')
      }
    }
  }
}
